import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { AuditPageDoc } from '../types';
import AuditHeader from '../components/AuditHeader';
import AuditTitle from '../components/AuditTitle';
import KpiCards from '../components/KpiCards';
import ExecutiveSummary from '../components/ExecutiveSummary';
import CompetitorTable from '../components/CompetitorTable';
import PhotoComparison from '../components/PhotoComparison';
import BreakdownChart from '../components/BreakdownChart';
import WeakPoints from '../components/WeakPoints';
import BusinessImpact from '../components/BusinessImpact';
import PlanCards from '../components/PlanCards';
import ImprovementRoadmap from '../components/ImprovementRoadmap';
import ForecastChart from '../components/ForecastChart';
import OpportunityLoss from '../components/OpportunityLoss';
import CompetitorThreats from '../components/CompetitorThreats';
import SubnicheOpportunities from '../components/SubnicheOpportunities';
import PhotoQuality from '../components/PhotoQuality';
import DistanceInsightSection from '../components/DistanceInsightSection';
import StepBar from '../components/StepBar';
import { useViewTracking } from '../hooks/useViewTracking';

const ShareAuditPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [doc, setDoc] = useState<AuditPageDoc | null>(null);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);

  useViewTracking(token);

  useEffect(() => {
    if (!token) {
      setError('Invalid link');
      setLoading(false);
      return;
    }
    fetch(`/api/audit/${token}`)
      .then(async r => {
        if (r.status === 404) throw new Error('リンクが見つかりません');
        if (r.status === 410) throw new Error('このリンクは期限切れです');
        if (!r.ok) throw new Error('読み込みエラー');
        return (await r.json()) as AuditPageDoc;
      })
      .then(d => setDoc(d))
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">読み込み中…</div>;
  }
  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <p className="text-base font-semibold text-slate-900 mb-1">{error}</p>
        <p className="text-xs text-slate-500">URLを確認するか、共有された方に再発行をご依頼ください。</p>
      </div>
    );
  }
  if (!doc) return null;

  const p = doc.payload;
  const generatedAt = new Date(doc.published_at).toLocaleDateString('ja-JP');

  return (
    <div className="min-h-screen bg-white">
      <AuditHeader generatedAt={generatedAt} />

      <AuditTitle payload={p} />

      <div className="max-w-[1400px] mx-auto px-6">
        {/* ============ Step 1: 現状診断 ============ */}
        <StepBar step="Step 1" title="現状診断" />
        <KpiCards payload={p} />
        <ExecutiveSummary text={p.executive_summary} />
        {p.opportunity_loss && <OpportunityLoss data={p.opportunity_loss} />}
        {p.distance_insight && <DistanceInsightSection data={p.distance_insight} station={p.station} />}

        {/* ============ Step 2: 競合分析 ============ */}
        <StepBar step="Step 2" title="競合分析" />
        <CompetitorTable rows={p.competitors} />
        {p.competitor_threats && p.competitor_threats.length > 0 && (
          <CompetitorThreats threats={p.competitor_threats} />
        )}
        <PhotoComparison payload={p} />
        {p.photo_analysis && <PhotoQuality analysis={p.photo_analysis} />}
        <BreakdownChart breakdown={p.health_breakdown} />

        {/* ============ Step 3: 改善計画 ============ */}
        <StepBar step="Step 3" title="改善計画" />
        <WeakPoints items={p.weak_with_commentary} recommendation={p.strategic_recommendation} />
        {p.subniche_opportunities && p.subniche_opportunities.length > 0 && (
          <SubnicheOpportunities items={p.subniche_opportunities} />
        )}
        {p.roadmap && <ImprovementRoadmap roadmap={p.roadmap} />}

        {/* ============ Step 4: 期待効果＆サービス ============ */}
        <StepBar step="Step 4" title="期待効果＆サービス" />
        {p.forecast && <ForecastChart forecast={p.forecast} />}
        <BusinessImpact predicted={p.predicted} narrative={p.business_impact} />
        <PlanCards mapsUrl={p.maps_url} />
      </div>

      <footer className="py-6 mt-8 text-center text-xs text-slate-400 border-t border-slate-200 bg-white">
        Powered by Clariveil
      </footer>
    </div>
  );
};

export default ShareAuditPage;
